import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { LowerElement } from "../../../../../http/vertigrip/types/lowerElements";
import { useLowerElementsApi } from "../../../../../http/vertigrip/useLowerElements";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateLowerElementModalDialogButton from "./CreateLowerElementModalDialogButton";
import EditLowerElementModalDialog from "./EditLowerElementModalDialog";

type Props = {
  active?: boolean;
};

const SteelVersionLowerElements: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { steelVersionId } = useParams<{ steelVersionId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "steelVersionLowerElements" });
  const aborter = useRef(new AbortController());
  const lowerElementaApi = useLowerElementsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "steelVersionLowerElements");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await lowerElementaApi.updateAdminLowerElementVisibility(id, { visibility }, { steelVersionId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (lowerElementId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await lowerElementaApi.updateAdminLowerElementOrder(lowerElementId, { order: newOrder }, { steelVersionId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [idItemToEdit, setIdItemToEdit] = useState<string>();
  const [itemToDelete, setItemToDelete] = useState<LowerElement>();
  const { accessesAction, accessesDialog } = useEntityAccesses<LowerElement>("vertigrip.lower_element");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = lowerElementaApi.useSteelVersionAdminLowerElements(
    steelVersionId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.lowerElements.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.lowerElements.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateLowerElementModalDialogButton steelVersionId={steelVersionId} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<LowerElement>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.installationTypes.singular}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/installation_types/${item.installationType?.id}`}
              >
                {item.installationType?.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setIdItemToEdit(item.id)}
        rowActions={(item) => [
          getEditAction(i18n, () => setIdItemToEdit(item?.id)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.lowerElements.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                lowerElementaApi
                  .deleteAdminLowerElement(
                    itemToDelete.id,
                    itemToDelete.vertigripSteelVersion.id,
                    itemToDelete.installationType.id,
                  )
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.lowerElements.questions.delete}
        </ModalDialog>
      )}
      <EditLowerElementModalDialog
        onClose={() => setIdItemToEdit(undefined)}
        steelVersionId={steelVersionId}
        lowerElementId={idItemToEdit}
      />
    </FlexContent>
  );
};

export default SteelVersionLowerElements;
